// Byrån ändrar offerten (ADR 0069): bolagsformen, vilka tjänster som ingår
// eller är tillval, antalen och drivarna. Räkna om visar priset från
// prismotorn innan offerten godkänns.
import { useId, useState } from 'react';
import { lasHeltal } from '../katalog/belopp.js';
import type { ServiceCatalog } from '../katalog/sammanfattning.js';
import { BOLAGSFORMER, drivare, prisText, type LegalForm } from '../katalog/texter.js';
import {
  andraAntal,
  andraDrivare,
  andraVal,
  drivareFor,
  drivareText,
  valAv,
  type Urval,
  type Val,
} from './urval.js';

const VAL: { kod: Val; namn: string }[] = [
  { kod: 'ingar', namn: 'Ingår' },
  { kod: 'tillval', namn: 'Tillval' },
  { kod: 'nej', namn: 'Inte med' },
];

export function Andra({
  katalog,
  urval,
  onChange,
  onRakna,
  onAvbryt,
  raknar,
  fel,
}: {
  katalog: ServiceCatalog;
  urval: Urval;
  onChange: (u: Urval) => void;
  onRakna: () => void;
  onAvbryt: () => void;
  raknar: boolean;
  fel: string | null;
}) {
  const id = useId();
  // Texten i drivarnas fält, så att ett intervall från AI står kvar tills byrån skriver en siffra.
  const [drivartext, setDrivartext] = useState<Record<string, string>>({});
  const drivarna = drivareFor(katalog, urval);
  const valda = [...urval.services, ...urval.optionalServices];
  const ogiltigt = valda.some((s) => s.quantity !== undefined && s.quantity < 1);

  return (
    <form
      className="andra"
      onSubmit={(e) => {
        e.preventDefault();
        onRakna();
      }}
    >
      <div className="tva">
        <div className="falt">
          <label className="etikett" htmlFor={`${id}-form`}>
            Bolagsform
          </label>
          <select
            id={`${id}-form`}
            className="in"
            value={urval.legalForm}
            onChange={(e) => onChange({ ...urval, legalForm: e.target.value as LegalForm })}
          >
            {BOLAGSFORMER.map((b) => (
              <option key={b.kod} value={b.kod}>
                {b.namn}
              </option>
            ))}
          </select>
        </div>
        {drivarna.map((kod) => {
          const text = drivartext[kod] ?? drivareText(urval, kod);
          const tal = lasHeltal(text);
          return (
            <div className="falt" key={kod}>
              <label className="etikett" htmlFor={`${id}-d-${kod}`}>
                {drivare(kod).namn}
              </label>
              <input
                id={`${id}-d-${kod}`}
                className="in mono"
                inputMode="numeric"
                value={text}
                aria-describedby={`${id}-d-${kod}-hjalp`}
                onChange={(e) => {
                  setDrivartext({ ...drivartext, [kod]: e.target.value });
                  const nytt = lasHeltal(e.target.value);
                  if (nytt !== null && nytt >= 0) onChange(andraDrivare(urval, kod, nytt));
                }}
              />
              <div className="hjalp" id={`${id}-d-${kod}-hjalp`}>
                {urval.drivers[kod]?.source === 'estimated' && tal === null
                  ? 'Uppskattat av AI. Skriv en siffra för att ändra.'
                  : 'Angivet'}
              </div>
            </div>
          );
        })}
      </div>
      <div className="tabellrulle" tabIndex={0} role="region" aria-label="Tjänsterna i katalogen">
        <table className="tabell andra-tabell">
          <caption className="visuellt-dold">Tjänsterna i katalogen</caption>
          <thead>
            <tr>
              <th scope="col">Tjänst</th>
              <th scope="col">I offerten</th>
              <th scope="col">Antal</th>
            </tr>
          </thead>
          <tbody>
            {katalog.services.map((s) => {
              const val = valAv(urval, s.id);
              const post = valda.find((v) => v.serviceId === s.id);
              return (
                <tr key={s.id} className={val === 'nej' ? 'inte-med' : undefined}>
                  <th scope="row">
                    {s.label}
                    <div className="hjalp">{prisText(s.pricing)}</div>
                  </th>
                  <td>
                    <label className="visuellt-dold" htmlFor={`${id}-val-${s.id}`}>
                      {s.label} i offerten
                    </label>
                    <select
                      id={`${id}-val-${s.id}`}
                      className="in"
                      value={val}
                      onChange={(e) =>
                        onChange(andraVal(urval, katalog, s.id, e.target.value as Val))
                      }
                    >
                      {VAL.map((v) => (
                        <option key={v.kod} value={v.kod}>
                          {v.namn}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {s.pricing.type === 'perUnit' && val !== 'nej' ? (
                      <>
                        <label className="visuellt-dold" htmlFor={`${id}-antal-${s.id}`}>
                          Antal {s.pricing.unit} för {s.label}
                        </label>
                        <input
                          id={`${id}-antal-${s.id}`}
                          className="in mono tal"
                          type="number"
                          min={1}
                          step={1}
                          value={post?.quantity ?? 1}
                          onChange={(e) =>
                            onChange(andraAntal(urval, s.id, Math.trunc(Number(e.target.value))))
                          }
                        />
                      </>
                    ) : (
                      <span className="hjalp">–</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {fel && (
        <div className="meddelande" role="alert">
          {fel}
        </div>
      )}
      <div className="panel-fot">
        <button type="button" className="btn btn-g" onClick={onAvbryt}>
          Avbryt ändringen
        </button>
        <button
          type="submit"
          className="btn btn-s"
          disabled={raknar || ogiltigt || urval.services.length === 0}
        >
          {raknar ? 'Räknar …' : 'Räkna om priset'}
        </button>
      </div>
    </form>
  );
}
